// ─────────────────────────────────────────────────────────────────────────────
// PARCOURS TERRAIN — 9 ÉTAPES, dictées par JJ (N°775, plan validé au N°777).
//
// 🔴 ÉCRAN NEUF. ⛔ Il ⛔ ne TOUCHE PAS au DAL de TEF (`MobileDALPage`) ni au
//    Visit Engine existant (`MobileVisitePage`) : ce sont des écrans distincts,
//    et le questionnaire logement de TEF ⛔ n'a rien à faire ici.
//
// LES 9 ÉTAPES, ⛔ ni une de plus, ⛔ ni une de moins :
//   1 choisir UN OU PLUSIEURS types de travaux
//   2 choisir le TYPE DE LOCAL
//   3 champs complémentaires — 🔴 JAMAIS bloquants
//   4 valider les postes détaillés de chaque type choisi
//   5 dans un poste, choisir les travaux EN DICTÉE ⇒ le descriptif vient du CATALOGUE
//   6 saisir la QUANTITÉ — dictée OU métrés (L × H = surface)
//   7 poste suivant
//   8 valider
//   9 ⇒ un RAPPORT DE VISITE et un DEVIS
//
// 🔴 RÈGLES ABSOLUES, tenues par construction :
//   · ON REVIENT TOUJOURS EN ARRIÈRE : `etape` est un simple index, et le bouton
//     « Retour » est présent à CHAQUE étape sauf la première. ⛔ Aucun point de
//     non-retour, ⛔ aucune étape qui se referme.
//   · ⛔ AUCUN CHAMP OBLIGATOIRE hors des 9 étapes : ⛔ pas d'année, ⛔ pas de
//     surface de logement. L'étape 3 est entièrement facultative et le DIT.
//   · ⛔ Le mot « logement » ⛔ n'est PAS un cadre : le cadre est le TYPE DE LOCAL
//     de l'étape 2, et « logement » n'y est qu'UNE valeur parmi cinq.
//
// 🔑 CE QUI EST RÉUTILISÉ, ⛔ pas réinventé — tout le backend existait déjà :
//   · `POST /api/visite/saisies` — ouvre (ou REPREND) la visite du dossier.
//   · `POST /api/visite/saisies/{id}/devis/proposer` — 🔴 LA DICTÉE EST DÉJÀ
//     APPARIÉE AU CATALOGUE DU TENANT côté serveur, et rend `description`,
//     `unite`, `prix_ht`, `prix_provisoire`, `taux_tva`. ⛔ Aucun appariement
//     refait ici : l'étape 5 AFFICHE ce que le serveur propose.
//   · `POST /api/visite/saisies/{id}/devis` — crée le devis (étape 9).
//   · `GET /api/visite/saisies/{id}/rapport` — le rapport, via `openAuthedPdf`
//     (SOURCE UNIQUE N°673 : un PDF se DEMANDE avec l'en-tête, il ⛔ ne se
//     NAVIGUE pas — un `<a href>` perd la session et rend 401).
//   · `GET /api/visite/saisies/{id}/catalogue` — 🔴 LA SEULE ROUTE CATALOGUE
//     ATTEIGNABLE DEPUIS UN LIEN DE MISSION (N°702). ⚠️ MESURÉ par CC SAV :
//     `/api/metier/catalogue/produits` rend **401** sur le lien, parce que le jeton
//     de mission est un cookie HttpOnly posé avec `path="/api/visite"` — un cookie
//     ⛔ n'est PAS envoyé hors de son chemin. ⇒ Le catalogue se demande SOUS la
//     visite, ⛔ pas par la route desktop.
//   · `metier_config_listes{type:"types_local"}` — l'autorité est le TENANT
//     (même principe que les types de RDV, N°742).
//     ⚠️ MAIS c'est une route DESKTOP : sur un lien de mission elle rend 401 elle
//     aussi. Le repli du code prend alors le relais — il porte EXACTEMENT la liste
//     validée par JJ, donc l'utilisateur voit la même chose. ⛔ Ce qu'il perd sur le
//     lien, c'est la possibilité pour le tenant de MODIFIER la liste ; ⛔ pas l'étape.
//
// 🔴 CONSÉQUENCE SUR L'ORDRE, et c'est structurel : le catalogue étant sous la
//    visite, LA VISITE S'OUVRE AVANT L'ÉTAPE 1. ⚠️ Ouvrir ⛔ n'écrit RIEN de
//    métier : `saisie.ouvrir` REND la visite en cours si elle existe (reprise), donc
//    revenir sur le parcours ⛔ ne crée PAS de doublon.
//
// ⚠️ LES 6 FAMILLES VIENNENT DU 2e SEGMENT DE LA RÉFÉRENCE (MB-**TECH**-PLOM), et
//    ⛔ PAS de `categorie` : mesuré le 2026-09-28, les 21 postes de mario_bat
//    portent TOUS `categorie: "renovation"` — ce champ ⛔ ne discrimine RIEN.
//    🔑 PLOMBERIE est donc dans « Technique » (segment TECH, 4 postes), et les
//    21 postes sont atteignables : TOIT 4 · FACE 3 · INTE 5 · MENU 4 · TECH 4 · CHAN 1.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Check, Loader2, Mic, Calculator,
  Home, Building2, Store, Layers, HelpCircle, FileText, Receipt,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { openAuthedPdf } from "../metier/dossier/helpers";
// 🔴 N°777-4 — installer la PWA est la PREMIÈRE étape du parcours.
import InvitationPWA from "./InvitationPWA";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ⚠️ Le profil de visite qui porte ce parcours. Mesuré : `mario_bat` déclare
//    `visite_travaux` (migrations 046/050). ⛔ Pas un profil inventé.
const PROFIL = "visite_travaux";

// 🔑 LES 6 FAMILLES — libellés validés par JJ au N°777. La clé est le SEGMENT de
//    la référence catalogue, ce qui rend le regroupement DÉRIVÉ de la donnée.
const FAMILLES = [
  { seg: "TOIT", label: "Toiture" },
  { seg: "FACE", label: "Façade" },
  { seg: "INTE", label: "Intérieur" },
  { seg: "MENU", label: "Menuiserie" },
  { seg: "TECH", label: "Technique" },
  { seg: "CHAN", label: "Chantier" },
];

// ⚠️ REPLI de l'étape 2, identique à la liste validée par JJ (N°777-1). L'autorité
//    reste le tenant : ce repli ⛔ ne sert QUE si la liste n'est pas encore seedée,
//    pour ⛔ ne PAS bloquer le parcours — la règle « jamais de blocage » passe avant.
const TYPES_LOCAL_REPLI = [
  { id: "immeuble", label: "Immeuble", icone: Building2 },
  { id: "maison", label: "Maison", icone: Home },
  { id: "logement", label: "Logement", icone: Home },
  { id: "local_commercial", label: "Local commercial", icone: Store },
  { id: "autre", label: "Autre", icone: HelpCircle },
];

const ICONES_LOCAL = { immeuble: Building2, maison: Home, logement: Home,
                       local_commercial: Store, autre: HelpCircle };

const entetes = () => {
  const t = localStorage.getItem("session_token");
  const tenant = localStorage.getItem("current_tenant_slug");
  return {
    "Content-Type": "application/json",
    ...(tenant && { ...enteteTenant(tenant) }),
    ...(t && { Authorization: `Bearer ${t}` }),
  };
};

const familleDe = (reference) => String(reference || "").split("-")[1] || "";

// ── Dictée : la même primitive que le reste du terrain ───────────────────────
// ⚠️ La reconnaissance vocale du navigateur ⛔ n'existe PAS partout (mesuré
//    ailleurs : elle dépend du MOTEUR, pas du système). Quand elle manque, le
//    champ texte reste utilisable : ⛔ jamais de blocage, c'est la règle du lot.
// ── LA DICTÉE — et surtout : ⛔ JAMAIS DE CUL-DE-SAC SILENCIEUX ────────────
//
// 🔴 CE QUI S'EST PASSÉ CHEZ MARIO (N°830, mesuré par CC SAV) : il ouvre le lien à
//    11:31:07, le catalogue charge en 200, puis **8 MINUTES DE SILENCE** — 0 dictée,
//    0 réponse, ⛔ aucune erreur, ⛔ aucun 4xx, ⛔ aucun 5xx. Il ⛔ n'a RIEN déclenché.
// 🔑 DEUX CAUSES, et aucune n'est une panne technique :
//    1. l'écran ⛔ ne DISAIT PAS quoi faire — un placeholder gris dans un champ
//       ressemble à du texte déjà saisi, et le bouton n'était QU'UNE icône de micro ;
//    2. sur Samsung Internet, `SpeechRecognition` peut EXISTER et ⛔ ne JAMAIS rendre
//       de résultat : le bouton passait au rouge et… rien. Aucun message, aucune sortie.
// ⇒ Un bouton rouge muet est pire qu'un bouton absent : l'utilisateur croit que ça marche.
// 🔴 N°846 — UN `detail` DE FASTAPI N'EST PAS TOUJOURS UNE CHAÎNE.
//
// ⚠️ Sur un 422 de validation, `detail` est une **LISTE D'OBJETS**
//    (`[{loc, msg, type}, …]`). `new Error(cetteListe)` donne littéralement
//    « [object Object] » — c'est ce que JJ a vu APRÈS une visite complète, et ça
//    ⛔ ne DISAIT PAS que la requête avait été refusée.
// 🔑 Cette fonction rend TOUJOURS une phrase lisible, et nomme le CHAMP fautif :
//    sans `loc`, on cherche au hasard ce que le serveur a refusé.
// ⚠️ Elle vaut pour TOUS les appels de cet écran, ⛔ pas seulement la clôture : un
//    `detail` structuré peut venir de n'importe quelle route.
function messageErreur(detail, statut) {
  if (!detail) return `Erreur ${statut}`;
  if (typeof detail === "string") return detail;
  const lignes = (Array.isArray(detail) ? detail : [detail]).map((d) => {
    if (typeof d === "string") return d;
    const champ = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
    const msg = d?.msg || d?.message || d?.detail || "champ refusé";
    return champ ? `${champ} : ${msg}` : String(msg);
  });
  return `Refusé par le serveur (${statut}) — ${lignes.join(" · ")}`;
}

const DELAI_SANS_RESULTAT_MS = 8000;

function useDictee(onTexte) {
  const [ecoute, setEcoute] = useState(false);
  const [message, setMessage] = useState(null);
  const dispo = typeof window !== "undefined"
    && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  const demarrer = useCallback(() => {
    if (!dispo) {
      // ⚠️ VISIBLE À L'ÉCRAN, ⛔ pas seulement un toast qui s'efface en 3 s.
      setMessage("La dictée n'est pas disponible sur ce navigateur. "
                 + "Écrivez les travaux dans le champ ci-dessus.");
      return;
    }
    const R = window.SpeechRecognition || window.webkitSpeechRecognition;
    let r;
    try { r = new R(); } catch {
      setMessage("La dictée n'a pas pu démarrer. Écrivez les travaux dans le champ.");
      return;
    }
    r.lang = "fr-FR";
    r.continuous = false;
    r.interimResults = false;

    // 🔴 LE GARDE-FOU DU N°833 : un délai SANS RÉSULTAT devient un MESSAGE.
    // ⚠️ C'est le cas Samsung : l'API répond `start()` sans erreur et ⛔ ne rend JAMAIS
    //    ni `onresult`, ni `onerror`, ni `onend`. Sans ce minuteur, l'écran reste rouge
    //    indéfiniment — c'est exactement les 8 minutes de Mario.
    let fini = false;
    const minuteur = setTimeout(() => {
      if (fini) return;
      fini = true;
      setEcoute(false);
      try { r.stop(); } catch { /* sans effet */ }
      setMessage("Je n'ai rien entendu. Vérifiez l'autorisation du micro, "
                 + "ou écrivez les travaux dans le champ ci-dessus.");
    }, DELAI_SANS_RESULTAT_MS);
    const terminer = () => { fini = true; clearTimeout(minuteur); setEcoute(false); };

    r.onresult = (e) => {
      terminer();
      setMessage(null);
      const t = e.results?.[0]?.[0]?.transcript;
      // ⚠️ Un résultat VIDE est un échec qui se DIT : ⛔ pas un silence de plus.
      if (t && String(t).trim()) onTexte(String(t));
      else setMessage("Je n'ai pas compris. Redites-le, ou écrivez-le dans le champ.");
    };
    r.onerror = (e) => {
      terminer();
      // 🔑 Le MOTIF distingue « micro refusé » de « rien entendu » : ⛔ ce ⛔ ne se corrige
      //    PAS de la même façon, et l'utilisateur doit savoir lequel des deux c'est.
      const code = e && e.error;
      setMessage(
        code === "not-allowed" || code === "service-not-allowed"
          ? "Le micro est refusé pour ce site. Autorisez-le dans le navigateur, "
            + "ou écrivez les travaux dans le champ ci-dessus."
          : code === "no-speech"
            ? "Je n'ai rien entendu. Réessayez, ou écrivez les travaux dans le champ."
            : "La dictée s'est arrêtée. Écrivez les travaux dans le champ ci-dessus.");
    };
    r.onend = () => { if (!fini) terminer(); };

    setMessage(null);
    setEcoute(true);
    try { r.start(); } catch {
      terminer();
      setMessage("La dictée n'a pas pu démarrer. Écrivez les travaux dans le champ.");
    }
  }, [dispo, onTexte]);

  return { ecoute, dispo, demarrer, message, effacerMessage: () => setMessage(null) };
}

// 🔴 N°833 — LE BOUTON PORTE UN LIBELLÉ, ⛔ pas qu'une icône. Mario est peintre : une
//    icône de micro seule ⛔ ne DIT PAS que c'est l'action principale de l'écran.
function BoutonDictee({ onTexte, testId, libelle = "Dicter" }) {
  const { ecoute, demarrer, message } = useDictee(onTexte);
  return (
    <div>
      <Button type="button" onClick={demarrer} data-testid={testId}
              aria-label={ecoute ? "Dictée en cours" : libelle}
              style={{ minHeight: 48, paddingLeft: 14, paddingRight: 14,
                       background: ecoute ? "var(--oh-stat-red-bg)" : "var(--oh-accent-solid)",
                       color: ecoute ? "var(--oh-stat-red-text)" : "var(--oh-accent-contrast)" }}>
        <Mic className="w-5 h-5 mr-2" />
        {ecoute ? "J'écoute…" : libelle}
      </Button>
      {/* ⚠️ LE MESSAGE EST DANS L'ÉCRAN, ⛔ pas dans un toast qui s'efface : Mario doit
          pouvoir le RELIRE pendant qu'il cherche l'autorisation du micro. */}
      {message && (
        <p className="text-xs mt-2" data-testid={`${testId}-message`}
           style={{ color: "var(--oh-stat-amber-text)" }}>{message}</p>
      )}
    </div>
  );
}

// ── Étape 6 : la quantité, par MÉTRÉS ou par dictée ─────────────────────────
// 🔴 « LE CALCUL FAIT PARTIE DU GESTE » (JJ) : on saisit 10 m × 3 m, ⛔ pas un m²
//    déjà calculé. Le produit s'affiche pendant la saisie.
function SaisieQuantite({ ligne, onChange }) {
  const [mode, setMode] = useState(ligne.unite === "m²" ? "metres" : "direct");
  const [L, setL] = useState(ligne.longueur || "");
  const [H, setH] = useState(ligne.hauteur || "");
  const nb = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
  const calcul = (nb(L) || 0) * (nb(H) || 0);

  useEffect(() => {
    if (mode !== "metres") return;
    // ⚠️ On garde AUSSI L et H : le devis doit pouvoir DIRE d'où vient la surface.
    onChange({ quantite: calcul || null, longueur: nb(L), hauteur: nb(H) });
  }, [L, H, mode]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {[["metres", "Métrés"], ["direct", `Quantité (${ligne.unite || "u"})`]].map(([m, lbl]) => (
          <button key={m} type="button" onClick={() => setMode(m)}
                  data-testid={`mode-${m}`}
                  className="text-xs px-3 rounded-lg"
                  style={{ minHeight: 44,
                           background: mode === m ? "var(--oh-accent-bg)" : "var(--oh-bg-surface)",
                           color: mode === m ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                           border: "0.5px solid var(--oh-border)" }}>
            {lbl}
          </button>
        ))}
      </div>

      {mode === "metres" ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input inputMode="decimal" value={L} onChange={(e) => setL(e.target.value)}
                 placeholder="Longueur (m)" data-testid="metre-longueur"
                 className="rounded-lg px-3 text-base" style={champ} />
          <span style={{ color: "var(--oh-text-muted)" }}>×</span>
          <input inputMode="decimal" value={H} onChange={(e) => setH(e.target.value)}
                 placeholder="Hauteur (m)" data-testid="metre-hauteur"
                 className="rounded-lg px-3 text-base" style={champ} />
          <span className="flex items-center gap-1 text-sm" data-testid="metre-resultat"
                style={{ color: "var(--oh-accent-text)" }}>
            <Calculator className="w-4 h-4" />
            {calcul ? `${calcul.toFixed(2)} ${ligne.unite || "m²"}` : "—"}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input inputMode="decimal" value={ligne.quantite ?? ""}
                 onChange={(e) => onChange({ quantite: nb(e.target.value) })}
                 placeholder={`Quantité en ${ligne.unite || "u"}`} data-testid="quantite-directe"
                 className="rounded-lg px-3 text-base" style={champ} />
          <BoutonDictee testId="quantite-dictee"
                        onTexte={(t) => {
                          // ⚠️ On extrait le PREMIER nombre dicté ; le reste est ignoré
                          //    plutôt que mal interprété.
                          const m = t.replace(",", ".").match(/[\d.]+/);
                          if (m) onChange({ quantite: Number(m[0]) });
                          else toast.info(`« ${t} » : aucun nombre reconnu.`);
                        }} />
        </div>
      )}
    </div>
  );
}

const champ = {
  minHeight: 44, minWidth: 110, background: "var(--oh-bg-surface)",
  border: "0.5px solid var(--oh-border)", color: "var(--oh-text)",
};


// ─────────────────────────────────────────────────────────────────────────────
// ACCÈS MISSION — le modèle du DAL, copié (N°786).
//
// 🔴 CE QUI ÉTAIT FAUX : ouvrir l'icône de la PWA menait à l'écran de connexion
//    EMAIL + MOT DE PASSE de l'ERP. Mario est sur un chantier : il ⛔ n'a PAS de
//    compte à saisir. Le DAL de Jordan, lui, demande CODE DOSSIER + PIN et ça
//    marche en production depuis des mois.
// 🔑 L'ÉQUIVALENT EXISTAIT DÉJÀ ici : `POST /api/visite/mission-session/{id}` avec
//    `{code}` — le SEUL point anonyme du module, qui rend une session de MISSION
//    (⛔ pas un compte, ⛔ pas un rôle), en cookie HttpOnly.
//    ⇒ ⛔ AUCUN mécanisme inventé : on met un écran devant la route qui existe.
// ⚠️ ET LE CODE EST RÉUTILISABLE (N°629) : le docstring de la route dit encore
//    « USAGE UNIQUE », c'est un commentaire PÉRIMÉ — `missions.echanger` explique
//    qu'un lien consommé serait MORT dès que Mario bascule vers l'appareil photo.
//    C'est ce qui rend cet écran utilisable plusieurs fois, comme le PIN du DAL.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ACCÈS À LA VISITE — ON DEMANDE CE QUE MARIO A, ⛔ PAS CE QU'IL N'A PAS.
//
// 🔴 MON ERREUR DE CONCEPTION, mesurée (N°836) : j'avais copié le modèle du DAL —
//    « référence + code » — SANS vérifier que le SMS de visite contient un code
//    LISIBLE. Mesure du texte envoyé (`visite/routes.py`) : le SMS porte
//    **UNIQUEMENT L'URL**. Le secret vit dans le fragment `#c=…`, que l'utilisateur
//    ⛔ ne VOIT PAS et ⛔ ne peut PAS recopier.
//    ⇒ Mon écran demandait donc à Mario deux choses que PERSONNE ⛔ ne lui a données.
// ⚠️ C'est ce qui a produit le cul-de-sac mesuré par INTÉGRATION : 3 ouvertures depuis
//    l'icône à 10:47–10:52, `GET /mission-session` → 401, puis un formulaire
//    inremplissable. Sur iOS, une PWA installée ⛔ ne PARTAGE PAS les cookies de
//    Safari : la session obtenue dans le navigateur ⛔ n'existe PAS dans l'application.
//
// 🔑 CE QU'IL A VRAIMENT : LE LIEN. Il peut le copier depuis son SMS. ⇒ On accepte le
//    LIEN COLLÉ, et on en extrait l'identifiant et le code. ⛔ Aucun mécanisme nouveau,
//    ⛔ aucun code à inventer côté serveur : c'est le même échange qu'au clic.
// ⚠️ Les deux champs manuels RESTENT, pour qui aurait les valeurs (support au téléphone),
//    mais ⛔ ils ⛔ ne sont PLUS le chemin principal.
function AccesMission({ onOuverte }) {
  const [lien, setLien] = useState("");
  const [mission, setMission] = useState(() => {
    try { return localStorage.getItem("chantier_derniere_mission") || ""; }
    catch { return ""; }
  });
  const [code, setCode] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState(null);
  const [manuel, setManuel] = useState(false);

  // 🔑 Extraction depuis le lien collé — tolérante, parce qu'un SMS se copie mal :
  //    espaces, retours à la ligne, lien tronqué, ou URL entière avec le domaine.
  const depuisLien = (brut) => {
    const t = String(brut || "").trim();
    if (!t) return null;
    const id = (t.match(/\/m\/([^/?#\s]+)/) || [])[1];
    const c = (t.match(/[#&?]c=([^&\s]+)/) || [])[1];
    return id && c ? { id: decodeURIComponent(id), code: decodeURIComponent(c) } : null;
  };

  const ouvrir = async (id, secret) => {
    setRefus(null);
    setEnvoi(true);
    try {
      const r = await fetch(`${API}/api/visite/mission-session/${encodeURIComponent(id)}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ code: secret }),
      });
      const d = await r.json().catch(() => ({}));
      // ⚠️ Le MOTIF du serveur est rendu TEL QUEL : « code expiré », « mission inconnue »
      //    et « mission révoquée » ⛔ ne se corrigent PAS de la même façon.
      // 🔴 L0 (N°852) — LE DERNIER `d.detail` BRUT DU PARCOURS, et c'est le PREMIER
      //    ÉCRAN QUE MARIO VOIT. `eb733f481` a protégé les trois appels du parcours
      //    (proposer · saisies · devis) et a LAISSÉ celui-ci : mesuré sur `origin/main`,
      //    3 sites passent par `messageErreur`, ⛔ celui-là non.
      // ⚠️ Un `detail` de 422 est une LISTE D'OBJETS ⇒ `new Error(liste)` donne
      //    « [object Object] », ici affiché par `setRefus(e.message)` juste dessous.
      //    ⛔ Le motif du serveur reste rendu tel quel quand c'est une chaîne :
      //    `messageErreur` la renvoie inchangée, donc « code expiré », « mission
      //    inconnue » et « mission révoquée » se distinguent toujours.
      if (!r.ok) throw new Error(messageErreur(d.detail, r.status));
      try { localStorage.setItem("chantier_derniere_mission", id); } catch { /* ⛔ pas bloquant */ }
      onOuverte(id);
    } catch (e) {
      setRefus(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  const entrerParLien = () => {
    const v = depuisLien(lien);
    if (!v) {
      // ⚠️ On DIT ce qui manque : un lien tronqué perd sa fin, et c'est la partie qui
      //    porte le secret. ⛔ Pas « lien invalide » sans dire quoi regarder.
      setRefus("Ce lien semble incomplet : il doit finir par « #c=… ». "
               + "Recopiez-le en entier depuis votre SMS.");
      return;
    }
    ouvrir(v.id, v.code);
  };

  return (
    <div className="min-h-screen px-4 py-8" style={{ background: "var(--oh-bg)" }}
         data-testid="acces-mission">
      <h1 className="text-xl mb-1" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
        Ouvrir ma visite
      </h1>
      <p className="text-sm mb-5" style={{ color: "var(--oh-text-secondary)" }}>
        Le plus simple : touchez le lien directement dans votre SMS.
        Sinon, copiez-le et collez-le ici.
      </p>

      <label className="block mb-3">
        <span className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
          Lien reçu par SMS
        </span>
        <input value={lien} onChange={(e) => setLien(e.target.value)}
               data-testid="acces-lien" autoComplete="off" inputMode="url"
               placeholder="Collez le lien entier"
               className="w-full rounded-lg px-3 mt-1 text-base" style={champ} />
      </label>

      {refus && (
        <div className="rounded-lg px-3 py-2 text-sm mb-3" data-testid="acces-mission-refus"
             style={{ background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>
          {refus}
        </div>
      )}

      <Button className="w-full" style={{ minHeight: 48 }} disabled={envoi || !lien.trim()}
              data-testid="acces-lien-entrer" onClick={entrerParLien}>
        {envoi ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ouvrir ma visite"}
      </Button>

      {/* ⚠️ Chemin de SECOURS, replié : il sert au support qui dicte les valeurs au
          téléphone. ⛔ Ce ⛔ n'est PLUS ce qu'on demande en premier. */}
      <button type="button" onClick={() => setManuel((m) => !m)}
              data-testid="acces-manuel-ouvrir"
              className="text-xs mt-4 underline" style={{ color: "var(--oh-text-muted)" }}>
        Je n'ai pas le lien
      </button>
      {manuel && (
        <div className="mt-3 space-y-2">
          <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
            Ces deux informations vous sont données par le bureau.
          </p>
          <input value={mission} onChange={(e) => setMission(e.target.value)}
                 data-testid="acces-mission-id" placeholder="Référence de la visite"
                 className="w-full rounded-lg px-3 text-base" style={champ} />
          <input value={code} onChange={(e) => setCode(e.target.value)}
                 data-testid="acces-mission-code" placeholder="Code d'accès"
                 className="w-full rounded-lg px-3 text-base" style={champ} />
          <Button className="w-full" style={{ minHeight: 48 }}
                  disabled={envoi || !mission.trim() || !code.trim()}
                  data-testid="acces-mission-entrer"
                  onClick={() => ouvrir(mission.trim(), code.trim())}>
            Ouvrir
          </Button>
        </div>
      )}

      <InvitationPWA />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function MobileChantierPage() {
  const { missionId } = useParams();
  const [etape, setEtape] = useState(1);
  const [catalogue, setCatalogue] = useState([]);
  const [typesLocal, setTypesLocal] = useState(TYPES_LOCAL_REPLI);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  // ── L'état du parcours ────────────────────────────────────────────────────
  const [familles, setFamilles] = useState([]);        // étape 1 (multi)
  const [typeLocal, setTypeLocal] = useState("");      // étape 2
  const [complements, setComplements] = useState({ adresse: "", notes: "" });  // étape 3
  const [postes, setPostes] = useState([]);            // étape 4 : références retenues
  const [lignes, setLignes] = useState([]);            // étapes 5-6
  const [idxPoste, setIdxPoste] = useState(0);         // étape 7
  const [visiteId, setVisiteId] = useState(null);
  const [dossierId, setDossierId] = useState(null);
  const [resultat, setResultat] = useState(null);      // étape 9
  // 🔑 Passe à vrai dès qu'une résolution de mission échoue : c'est ce qui fait
  //    apparaître l'accès mission au lieu d'un écran vide ou d'un login de compte.
  const [besoinAcces, setBesoinAcces] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  // ── Les types de local : route DESKTOP, donc 401 sur un lien de mission ───
  // ⚠️ L'échec est SILENCIEUX et NORMAL : le repli porte la même liste. ⛔ On ⛔ n'affiche
  //    PAS une erreur pour une liste de configuration qu'on sait remplacer.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/metier/config-listes/types_local`,
                              { headers: entetes(), credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        if (d.items?.length) {
          setTypesLocal(d.items.map((i) => ({
            id: i.id, label: i.label, icone: ICONES_LOCAL[i.id] || HelpCircle })));
        }
      } catch { /* ⛔ pas bloquant : le repli suffit */ }
    })();
  }, []);

  // ── L'ÉCHANGE DU LIEN, puis la visite, puis le catalogue ──────────────────
  //
  // 🔴 CE QUE J'AVAIS ÉCRIT ÉTAIT FAUX, et INTÉGRATION l'a mesuré À L'ÉCRAN EN PROD :
  //    j'appelais `GET /api/visite/missions/{id}` — **une route qui ⛔ N'EXISTE PAS**.
  //    Je l'avais supposée depuis le nom de la collection `visite_missions`, ⛔ sans la
  //    vérifier. Résultat : 401, « ce lien ne porte pas de dossier », 6 familles à
  //    « 0 poste » — et mon correctif du catalogue (N°779), juste, ⛔ n'était JAMAIS atteint.
  // ⚠️ ET SURTOUT je ⛔ n'ÉCHANGEAIS JAMAIS le code du fragment : sans `POST
  //    /mission-session/{id}`, le cookie de mission ⛔ n'est PAS posé, donc TOUT est 401.
  //
  // 🔑 LE MODÈLE COPIÉ, ⛔ pas réinventé — `MobileVisitePage.jsx:336-360` :
  //    1. le code vit dans le FRAGMENT `#c=…`, que le navigateur ⛔ n'envoie JAMAIS au
  //       serveur (⛔ ni journal nginx, ⛔ ni Referer) ⇒ on le lit ICI et on le POSTE ;
  //    2. SANS code (rechargement, ou icône PWA), on regarde si la SESSION vit encore
  //       via `GET /mission-session` — l'écran a effacé le secret de la barre d'adresse,
  //       un simple rechargement ⛔ ne doit PAS ramener un refus ;
  //    3. l'échec des deux ⇒ écran d'ACCÈS MISSION, ⛔ jamais le login de l'ERP.
  // ⚠️ `dossier_id` et `visite_id` viennent de la RÉPONSE de l'échange : ⛔ plus aucune
  //    route devinée, et ⛔ plus besoin d'ouvrir une visite (elle existe déjà).
  useEffect(() => {
    (async () => {
      try {
        const code = new URLSearchParams(
          (window.location.hash || "").replace(/^#/, "")).get("c");
        let session = null;
        if (missionId && code) {
          const r = await fetch(
            `${API}/api/visite/mission-session/${encodeURIComponent(missionId)}`,
            { method: "POST", headers: { "Content-Type": "application/json" },
              credentials: "include", body: JSON.stringify({ code }) });
          if (r.ok) {
            session = await r.json();
            // ⚠️ Le secret quitte la barre d'adresse dès qu'il est échangé : il ⛔ ne doit
            //    PAS rester dans l'historique ni dans un partage d'écran.
            try { window.history.replaceState(null, "", window.location.pathname); }
            catch { /* sans effet */ }
          }
        }
        if (!session) {
          // 🔑 Session déjà vivante (rechargement, retour depuis l'appareil photo, icône PWA).
          const r = await fetch(`${API}/api/visite/mission-session`,
                                { credentials: "include" });
          if (r.ok) session = await r.json();
        }
        if (!session) { setBesoinAcces(true); return; }

        const visite = session.visite_id || session.visiteId || null;
        const dossier = session.dossier_id || session.dossierId || null;
        setVisiteId(visite);
        setDossierId(dossier);
        if (missionId) {
          // ⛔ On mémorise l'identifiant SEUL, ⛔ JAMAIS le secret : la session vit en
          //    cookie HttpOnly, c'est elle qui autorise.
          try { localStorage.setItem("chantier_derniere_mission", missionId); }
          catch { /* ⛔ pas bloquant */ }
        }
        if (!visite) {
          setErreur("Cette mission ⛔ ne porte pas de visite : demandez un nouveau lien.");
          return;
        }

        // 🔑 LE CATALOGUE, SOUS LA VISITE — la seule route atteignable avec un jeton de
        //    mission (N°702/779). La route desktop rend 401 ici.
        const rc = await fetch(`${API}/api/visite/saisies/${visite}/catalogue`,
                               { credentials: "include" });
        if (!rc.ok) {
          setErreur(`Vos postes n'ont pas pu être chargés (${rc.status}).`);
          return;
        }
        const dc = await rc.json();
        const liste = Array.isArray(dc) ? dc : (dc.produits || dc.catalogue || dc.items || []);
        setCatalogue(liste.filter((p) => p.actif !== false));
        if (!liste.length) setErreur("Votre catalogue est vide : aucun poste à chiffrer.");
      } catch (e) {
        setErreur(e.message || "Réseau indisponible.");
      } finally {
        setChargement(false);
      }
    })();
  }, [missionId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const postesDeLaFamille = useCallback(
    (seg) => catalogue.filter((p) => familleDe(p.reference) === seg),
    [catalogue]);

  const postesRetenus = useMemo(
    () => catalogue.filter((p) => postes.includes(p.reference)), [catalogue, postes]);

  const posteCourant = postesRetenus[idxPoste];
  // 🔴 N°807 — LE POSTE D'AFFICHAGE ET LE PRODUIT APPARIÉ SONT DEUX CHOSES.
  // ⚠️ DÉFAUT MESURÉ À L'ÉCRAN EN PROD par INTÉGRATION : une ligne dictée pouvait
  //    DISPARAÎTRE EN SILENCE. « remplacement du chauffe-eau dans la salle de bain »
  //    apparie MB-INTE-SDE (« Salle d'eau », sur les mots « eau » et « salle »), et je
  //    rangeais la ligne sous CE produit ⇒ le filtre du poste courant (Plomberie) ⛔ ne
  //    l'affichait PAS. La ligne existait dans l'état, sous un poste que Mario ⛔ n'avait
  //    PAS retenu : il ⛔ ne la voyait JAMAIS, et ⛔ aucun message ⛔ ne le disait.
  // 🔑 Une ligne dictée DANS un poste reste visible DANS ce poste : `poste` gouverne
  //    l'AFFICHAGE, `reference` reste le produit apparié qui part au devis. Confondre les
  //    deux, c'était laisser la donnée décider de ce que l'utilisateur voit.
  const lignesDuPoste = lignes.filter((l) => l.poste === posteCourant?.reference);

  const majLigne = (cle, patch) => setLignes((ls) =>
    ls.map((l) => (l.cle === cle ? { ...l, ...patch } : l)));

  // ── Étape 5 : la dictée est appariée au catalogue PAR LE SERVEUR ──────────
  const proposerDepuisDictee = async (texte) => {
    if (!texte?.trim()) return;
    let id = visiteId;
    if (!id) { id = await ouvrirVisite(); if (!id) return; }
    try {
      const r = await fetch(`${API}/api/visite/saisies/${id}/devis/proposer`, {
        method: "POST", headers: entetes(), credentials: "include",
        body: JSON.stringify({ texte }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(messageErreur(d.detail, r.status));
      const props = d.propositions || d.lignes || [];
      if (!props.length) {
        // ⚠️ « rien de correspondant » est une RÉPONSE, ⛔ pas une panne (le serveur
        //    le dit explicitement). On le TRANSMET au lieu d'inventer un poste.
        toast.info(`« ${texte} » : aucun poste correspondant dans votre catalogue`
                   + (d.taille_catalogue ? ` (${d.taille_catalogue} postes).` : "."));
        return;
      }
      // 🔴 N°802 — LES CHAMPS SONT DANS `p.produit`, ⛔ PAS À LA RACINE.
      //
      // ⚠️ MESURÉ dans `core/intention/texte_vers_intentions.py` : une proposition vaut
      //    `{enonce, quantite, unite, manques[], candidats[], produit{reference,
      //    designation, unite, prix_ht, prix_provisoire, taux_tva}, lieu?, descriptif?,
      //    poste?, motif, a_valider}`.
      //    Je lisais `p.designation`, `p.description`, `p.prix_ht` — TOUS `undefined`,
      //    et `p.poste` peut être un OBJET. C'est de là que venait le « [object Object] »
      //    que JJ a vu après « porte de l'entrée de l'immeuble cochère ».
      // 🔑 `texte(v)` est la GARDE : ⛔ RIEN ne part à l'écran sans être une chaîne. C'est
      //    la même faute que le `[object Object]` des adresses (TEF-DOS-2026-00257), déjà
      //    payée en mai 2026 — un objet rendu directement ⛔ ne se voit qu'à l'écran.
      const texteSur = (v) => (v == null ? ""
        : typeof v === "string" ? v
        : typeof v === "number" ? String(v)
        // ⛔ Un objet ⛔ n'est JAMAIS affiché : on tente ses champs parlants, sinon rien.
        : (v.designation || v.libelle || v.nom || v.label || v.description || ""));
      setLignes((ls) => [...ls, ...props.map((p, i) => {
        const prod = p.produit || {};
        return {
          cle: `${Date.now()}-${i}`,
          // 🔑 Le POSTE où Mario a dicté : c'est lui qui range la ligne à l'écran.
          poste: posteCourant?.reference || null,
          // 🔑 Le PRODUIT apparié par le serveur : c'est lui qui part au devis.
          reference: prod.reference || posteCourant?.reference || null,
          // ⚠️ DIT quand le produit retenu appartient à un AUTRE poste : Mario doit pouvoir
          //    corriger plutôt que de découvrir la surprise sur le devis.
          hors_poste: Boolean(prod.reference && posteCourant?.reference
                              && prod.reference !== posteCourant.reference),
          // 🔑 La DÉSIGNATION du catalogue si un poste est reconnu, sinon ce qui a été dicté.
          designation: texteSur(prod.designation) || texteSur(p.enonce) || texte,
          // 🔑 CE QUE MARIO A DIT EN PLUS (`descriptif`) et OÙ (`lieu`) : c'est ça qui doit
          //    s'afficher, ⛔ pas la structure. ⚠️ Le descriptif du CATALOGUE est ajouté par
          //    le serveur au devis ; ici on montre ce qui vient de la dictée.
          // 🔴 N°820 — LE DESCRIPTIF DU CATALOGUE, repris DANS L'ÉTAT DE L'ÉCRAN.
          //
          // ⚠️ DÉFAUT MESURÉ EN PROD par INTÉGRATION : l'écran affichait « Aucun descriptif
          //    au catalogue pour ce poste » alors que le catalogue EN A UN — MB-TECH-PLOM
          //    porte « Dépose du ballon d'eau chaude existant… », et 6 postes sur 21 ont
          //    une `description`. Le libellé était donc FAUX, et un message faux est pire
          //    qu'un blanc : il envoie corriger la donnée au lieu de l'écran.
          // 🔑 LA CAUSE : `/devis/proposer` rend `produit{reference, designation, unite,
          //    prix_ht, prix_provisoire, taux_tva, score}` — ⛔ SANS `description` (vérifié
          //    dans `texte_vers_intentions.py`). Je lisais donc un champ qui ⛔ n'arrive
          //    JAMAIS, exactement comme je lisais `p.designation` au N°802.
          // 🔑 LA SOURCE EXISTE DÉJÀ CÔTÉ ÉCRAN : `catalogue` porte les 21 postes AVEC leur
          //    `description` (chargés sous la visite au démarrage). ⇒ On retrouve l'entrée
          //    par sa RÉFÉRENCE. ⛔ Aucun appel de plus, ⛔ aucune route nouvelle.
          // ⚠️ ORDRE VOULU : le descriptif du CATALOGUE d'abord (c'est le texte contractuel
          //    qui doit figurer au devis), puis ce que Mario a dicté en plus (lieu, reste).
          //    Le lieu seul ⛔ ne remplace PAS un descriptif absent.
          description: (() => {
            const fiche = catalogue.find((c) => c.reference === prod.reference);
            const duCatalogue = texteSur(fiche && fiche.description);
            const deLaDictee = [texteSur(p.lieu), texteSur(p.descriptif)]
              .filter(Boolean).join(" — ");
            return [duCatalogue, deLaDictee].filter(Boolean).join("\n");
          })(),
          unite: texteSur(p.unite) || texteSur(prod.unite) || "u",
          prix_ht: prod.prix_ht ?? null,
          prix_provisoire: prod.prix_provisoire ?? true,
          taux_tva: prod.taux_tva ?? null,
          // ⚠️ Le MOTIF du serveur (« Poste appelé — à confirmer ») est rendu : il dit à
          //    Mario si le poste a été APPELÉ ou DEVINÉ.
          motif: texteSur(p.motif),
          // ⚠️ Ce qui MANQUE est dit, ⛔ pas deviné : `manques` est une liste de chaînes.
          manques: Array.isArray(p.manques) ? p.manques.map(texteSur).filter(Boolean) : [],
          quantite: p.quantite ?? null,
        };
      })]);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ⚠️ Filet : la visite est normalement ouverte au chargement. Cette fonction ⛔ ne
  //    sert QUE si l'ouverture a échoué (réseau) — elle ⛔ ne crée PAS de seconde visite,
  //    `ouvrir` rendant la visite en cours.
  const ouvrirVisite = async () => {
    if (visiteId) return visiteId;
    if (!dossierId) {
      toast.error("Ce lien ⛔ ne porte pas de dossier : ouvrez le parcours depuis le dossier.");
      return null;
    }
    try {
      const r = await fetch(`${API}/api/visite/saisies`, {
        method: "POST", headers: entetes(), credentials: "include",
        body: JSON.stringify({ dossier_id: dossierId, profil_code: PROFIL }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(messageErreur(d.detail, r.status));
      setVisiteId(d.id);
      return d.id;
    } catch (e) {
      toast.error(e.message);
      return null;
    }
  };

  // ── Étapes 8 et 9 : valider ⇒ rapport + devis ────────────────────────────
  const valider = async () => {
    setEnvoi(true);
    try {
      let id = visiteId || await ouvrirVisite();
      if (!id) return;
      const retenues = lignes.filter((l) => l.quantite);
      const r = await fetch(`${API}/api/visite/saisies/${id}/devis`, {
        method: "POST", headers: entetes(), credentials: "include",
        // 🔴 N°846 — LE CORPS EXACT QUE LE SERVEUR ACCEPTE, ⛔ pas celui que j'avais inventé.
        //
        // ⚠️ MESURÉ dans `visite/routes.py` : `ValidationDevis` et `LigneValidee` portent TOUS
        //    DEUX `extra="forbid"`, et les seuls champs déclarés sont
        //    `lignes_validees[{produit_reference, quantite, designation, poste, lieu}]`.
        //    J'envoyais `lignes[{reference, description, unite, prix_ht, taux_tva, longueur,
        //    hauteur}]` + `type_local` + `notes` ⇒ CHAQUE champ était refusé ⇒ **422**.
        // 🔑 Le docstring du modèle le DIT en toutes lettres : « Envoyer `prix_ht` fait REFUSER
        //    la requête, ⛔ il n'est pas ignoré en silence » — un navigateur ⛔ ne propose PAS
        //    le prix, le serveur le relit du catalogue. Je ne l'avais pas lu.
        // ⇒ C'est ce 422 que l'écran affichait en « [object Object] » : le devis ⛔ n'était
        //   JAMAIS créé, et la visite ⛔ jamais clôturée.
        // ⛔ `poste` ⛔ N'EST PAS ENVOYÉ : le modèle l'attend en `int` alors que le mien est une
        //    RÉFÉRENCE de catalogue (« MB-INTE-PEIN ») — l'envoyer referait un 422.
        body: JSON.stringify({
          lignes_validees: retenues.map((l) => ({
            produit_reference: l.reference,
            quantite: l.quantite,
            designation: l.designation || undefined,
            lieu: l.lieu || undefined,
          })),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(messageErreur(d.detail, r.status));
      setResultat({ visiteId: id, devis: d });
      setEtape(9);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEnvoi(false);
    }
  };

  const ouvrirRapport = () => openAuthedPdf({
    url: `${API}/api/visite/saisies/${resultat?.visiteId}/rapport`,
    headers: entetes(),
    onError: (msg) => toast.error(msg),
  });

  // ═══ Rendu ═══════════════════════════════════════════════════════════════
  const TITRES = {
    1: "Types de travaux", 2: "Type de local", 3: "Informations complémentaires",
    4: "Postes à chiffrer", 5: "Travaux et quantités", 8: "Valider la visite",
    9: "Visite terminée",
  };

  // 🔴 N°786 — SANS MISSION, ON MONTRE L'ACCÈS MISSION, ⛔ JAMAIS le login de l'ERP.
  // ⚠️ `besoinAcces` se déclenche aussi quand la session de visite a EXPIRÉ : Mario
  //    ressaisit ses identifiants, ⛔ il ⛔ ne tombe PAS sur un écran de compte.
  if (!chargement && !visiteId && (besoinAcces || (!missionId && !dossierId))) {
    return <AccesMission onOuverte={(m) => {
      // ⚠️ On RECHARGE sur l'URL de la mission : le reste du parcours suppose un
      //    `missionId` dans le chemin, ⛔ on ⛔ ne duplique PAS sa résolution.
      window.location.replace(`/mobile/chantier/m/${m}`);
    }} />;
  }

  if (chargement) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: "var(--oh-bg)" }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-accent-text)" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28" style={{ background: "var(--oh-bg)" }}
         data-testid="parcours-chantier">
      <header className="px-4 py-3 sticky top-0 z-10"
              style={{ background: "var(--oh-bg-elevated)",
                       borderBottom: "0.5px solid var(--oh-border)" }}>
        <div className="flex items-center gap-2">
          {/* 🔴 RETOUR TOUJOURS POSSIBLE — sauf à la 1re étape, où il n'y a rien derrière. */}
          {etape > 1 && etape < 9 && (
            <Button variant="ghost" onClick={() => setEtape((e) => (e === 8 ? 5 : e - 1))}
                    data-testid="etape-retour" aria-label="Revenir à l'étape précédente"
                    style={{ minHeight: 44, minWidth: 44 }}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              {etape <= 8 ? `Étape ${etape} sur 8` : "Terminé"}
            </p>
            <h1 className="text-base" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
              {TITRES[etape] || TITRES[5]}
            </h1>
          </div>
        </div>
      </header>

      {/* 🔴 ÉTAPE 0 DU PARCOURS : l'invite disparaît d'elle-même une fois installée. */}
      {etape === 1 && <InvitationPWA />}

      {erreur && (
        <div className="mx-4 mt-3 rounded-lg px-3 py-2 text-sm"
             style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>
          {erreur}
        </div>
      )}

      <main className="px-4 py-4 space-y-3">
        {/* ── 1 · TYPES DE TRAVAUX (multi) ── */}
        {etape === 1 && FAMILLES.map((f) => {
          const n = postesDeLaFamille(f.seg).length;
          const actif = familles.includes(f.seg);
          return (
            <button key={f.seg} type="button" data-testid={`famille-${f.seg}`}
                    onClick={() => setFamilles((l) =>
                      actif ? l.filter((x) => x !== f.seg) : [...l, f.seg])}
                    className="w-full text-left rounded-xl px-4 py-3 flex items-center justify-between"
                    style={{ minHeight: 56,
                             background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg-elevated)",
                             border: `0.5px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                             color: actif ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
              <span className="flex items-center gap-2">
                <Layers className="w-4 h-4" /> {f.label}
                <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                  {n} poste{n > 1 ? "s" : ""}
                </span>
              </span>
              {actif && <Check className="w-5 h-5" />}
            </button>
          );
        })}

        {/* ── 2 · TYPE DE LOCAL ── */}
        {etape === 2 && typesLocal.map((t) => {
          const I = t.icone || HelpCircle;
          const actif = typeLocal === t.id;
          return (
            <button key={t.id} type="button" data-testid={`local-${t.id}`}
                    onClick={() => setTypeLocal(t.id)}
                    className="w-full text-left rounded-xl px-4 py-3 flex items-center gap-2"
                    style={{ minHeight: 56,
                             background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg-elevated)",
                             border: `0.5px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                             color: actif ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
              <I className="w-4 h-4" /> {t.label}
              {actif && <Check className="w-5 h-5 ml-auto" />}
            </button>
          );
        })}

        {/* ── 3 · COMPLÉMENTS — 🔴 JAMAIS BLOQUANTS, et l'écran le DIT ── */}
        {etape === 3 && (
          <>
            <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              Facultatif. Vous pouvez passer cette étape.
            </p>
            {[["adresse", "Adresse du chantier"], ["notes", "Notes"]].map(([k, lbl]) => (
              <label key={k} className="block">
                <span className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>{lbl}</span>
                <div className="flex gap-2 mt-1">
                  <input value={complements[k]} data-testid={`complement-${k}`}
                         onChange={(e) => setComplements((c) => ({ ...c, [k]: e.target.value }))}
                         className="flex-1 rounded-lg px-3 text-base" style={champ} />
                  <BoutonDictee testId={`complement-dictee-${k}`}
                                onTexte={(t) => setComplements((c) => ({ ...c, [k]: t }))} />
                </div>
              </label>
            ))}
          </>
        )}

        {/* ── 4 · POSTES DÉTAILLÉS de chaque famille choisie ── */}
        {etape === 4 && familles.map((seg) => (
          <div key={seg} className="space-y-2">
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>
              {FAMILLES.find((f) => f.seg === seg)?.label}
            </p>
            {postesDeLaFamille(seg).map((p) => {
              const actif = postes.includes(p.reference);
              return (
                <button key={p.reference} type="button" data-testid={`poste-${p.reference}`}
                        onClick={() => setPostes((l) =>
                          actif ? l.filter((x) => x !== p.reference) : [...l, p.reference])}
                        className="w-full text-left rounded-xl px-4 py-3 flex items-center justify-between"
                        style={{ minHeight: 56,
                                 background: actif ? "var(--oh-accent-bg)" : "var(--oh-bg-elevated)",
                                 border: `0.5px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                                 color: actif ? "var(--oh-accent-text)" : "var(--oh-text)" }}>
                  <span>
                    {p.designation || p.reference}
                    <span className="block text-xs" style={{ color: "var(--oh-text-muted)" }}>
                      {p.unite} · {p.prix_ht != null ? `${p.prix_ht} € HT` : "prix à définir"}
                      {p.prix_provisoire ? " (provisoire)" : ""}
                    </span>
                  </span>
                  {actif && <Check className="w-5 h-5" />}
                </button>
              );
            })}
          </div>
        ))}

        {/* ── 5 et 6 · TRAVAUX EN DICTÉE puis QUANTITÉ, poste par poste (7) ── */}
        {etape === 5 && posteCourant && (
          <>
            <div className="rounded-xl px-4 py-3"
                 style={{ background: "var(--oh-bg-elevated)",
                          border: "0.5px solid var(--oh-border)" }}>
              <p style={{ color: "var(--oh-text)", fontWeight: 500 }}>
                {posteCourant.designation}
              </p>
              <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Poste {idxPoste + 1} sur {postesRetenus.length}
              </p>
            </div>

            {/* 🔴 N°833 — L'INSTRUCTION EST VISIBLE, ⛔ ce ⛔ n'est PLUS un placeholder gris.
                ⚠️ Mesuré chez Mario : 8 minutes sans un seul appel réseau. Il ⛔ n'a pas
                   essayé puis échoué — il ⛔ n'a RIEN déclenché, faute de savoir où cliquer.
                🔑 Un placeholder ressemble à du texte DÉJÀ SAISI et disparaît au premier
                   caractère : ⛔ ce ⛔ n'est PAS une consigne. Celle-ci reste affichée. */}
            <div className="rounded-xl px-4 py-3" data-testid="etape5-consigne"
                 style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}>
              <p style={{ fontWeight: 500 }}>Appuyez sur « Dicter » et dites les travaux</p>
              <p className="text-xs mt-1" style={{ color: "var(--oh-text-secondary)" }}>
                Par exemple : « peinture des murs ». Vous pouvez aussi les écrire.
                Ajoutez-en autant que nécessaire, puis indiquez les quantités.
              </p>
            </div>

            {/* 🔑 LE BOUTON D'ABORD, en pleine largeur : c'est l'action principale de l'écran.
                ⚠️ Le champ texte reste dessous et TOUJOURS utilisable — le micro refusé ou
                   muet ⛔ ne doit JAMAIS être un cul-de-sac (règle du N°833). */}
            <BoutonDictee testId="travaux-dictee" onTexte={proposerDepuisDictee}
                          libelle="Dicter les travaux" />

            <div className="flex gap-2 items-center">
              <input placeholder="… ou écrivez ici (ex. peinture mur), puis Entrée"
                     data-testid="travaux-texte" className="flex-1 rounded-lg px-3 text-base"
                     style={champ}
                     onKeyDown={(e) => {
                       if (e.key === "Enter") { proposerDepuisDictee(e.target.value); e.target.value = ""; }
                     }} />
              {/* ⚠️ Un bouton EXPLICITE pour valider la saisie clavier : « Entrée » ⛔ n'est
                  PAS évident sur un clavier mobile, et c'est le chemin de repli. */}
              <Button type="button" data-testid="travaux-ajouter" style={{ minHeight: 48 }}
                      onClick={() => {
                        const el = document.querySelector('[data-testid="travaux-texte"]');
                        if (el && el.value.trim()) { proposerDepuisDictee(el.value); el.value = ""; }
                      }}>
                Ajouter
              </Button>
            </div>

            {lignesDuPoste.map((l) => (
              <div key={l.cle} className="rounded-xl px-4 py-3 space-y-2"
                   data-testid={`ligne-${l.cle}`}
                   style={{ background: "var(--oh-bg-elevated)",
                            border: "0.5px solid var(--oh-border)" }}>
                <div className="flex items-start justify-between gap-2">
                  <p style={{ color: "var(--oh-text)", fontWeight: 500 }}>{l.designation}</p>
                  {/* 🔴 SUPPRIMER UN TRAVAIL DICTÉ (demande de JJ) : une dictée se trompe,
                      et une ligne fausse ⛔ ne doit PAS obliger à recommencer le poste.
                      ⚠️ 44px de cible : c'est un doigt sur un chantier, ⛔ pas une souris. */}
                  <button type="button" aria-label={`Supprimer ${l.designation}`}
                          data-testid={`ligne-supprimer-${l.cle}`}
                          onClick={() => setLignes((ls) => ls.filter((x) => x.cle !== l.cle))}
                          style={{ minHeight: 44, minWidth: 44, borderRadius: 8,
                                   color: "var(--oh-stat-red-text)",
                                   background: "var(--oh-stat-red-bg)" }}>
                    ✕
                  </button>
                </div>
                {/* 🔴 N°807 — LE DÉCALAGE EST DIT, ⛔ pas caché : le poste apparié n'est pas
                    celui où Mario a dicté. Il peut supprimer la ligne et redicter. */}
                {l.hors_poste && (
                  <p className="text-xs" data-testid="ligne-hors-poste"
                     style={{ color: "var(--oh-stat-amber-text)" }}>
                    ⚠️ Poste retenu au catalogue : {l.designation} — différent du poste en cours.
                  </p>
                )}
                {/* ⚠️ Le motif du serveur : « appelé » ⛔ n'est pas « deviné ». */}
                {l.motif && (
                  <p className="text-xs" data-testid="ligne-motif"
                     style={{ color: "var(--oh-text-muted)" }}>{l.motif}</p>
                )}
                {/* 🔑 LE DESCRIPTIF DU CATALOGUE, ajouté par l'appli — étape 5. */}
                {/* ⚠️ Un descriptif PEUT MANQUER : CC SAV les pose poste par poste, et
                    certains resteront vides faute de source (JJ les écrira). On le DIT
                    au lieu de laisser un blanc qui ferait croire à un bug. */}
                {l.description ? (
                  <p className="text-xs whitespace-pre-line" data-testid="ligne-descriptif"
                     style={{ color: "var(--oh-text-secondary)" }}>{l.description}</p>
                ) : (
                  <p className="text-xs" data-testid="ligne-sans-descriptif"
                     style={{ color: "var(--oh-stat-amber-text)" }}>
                    Aucun descriptif au catalogue pour ce poste — il sera à compléter sur le devis.
                  </p>
                )}
                <SaisieQuantite ligne={l} onChange={(patch) => majLigne(l.cle, patch)} />
              </div>
            ))}
          </>
        )}

        {/* ── 8 · VALIDER ── */}
        {etape === 8 && (
          <div className="space-y-2">
            <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
              {lignes.filter((l) => l.quantite).length} travaux chiffrés ·
              {" "}{typesLocal.find((t) => t.id === typeLocal)?.label || "local non précisé"}
            </p>
            {lignes.filter((l) => l.quantite).map((l) => (
              <div key={l.cle} className="rounded-lg px-3 py-2 text-sm"
                   style={{ background: "var(--oh-bg-surface)",
                            border: "0.5px solid var(--oh-border)" }}>
                <span style={{ color: "var(--oh-text)" }}>{l.designation}</span>
                <span className="float-right" style={{ color: "var(--oh-accent-text)" }}>
                  {l.quantite} {l.unite}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── 9 · RAPPORT + DEVIS ── */}
        {etape === 9 && resultat && (
          <div className="space-y-3">
            <div className="rounded-xl px-4 py-3" data-testid="parcours-termine"
                 style={{ background: "var(--oh-stat-green-bg)",
                          color: "var(--oh-stat-green-text)" }}>
              Visite enregistrée.
              {resultat.devis?.numero || resultat.devis?.numero_provisoire
                ? ` Devis ${resultat.devis.numero || resultat.devis.numero_provisoire}.`
                : ""}
            </div>
            <Button onClick={ouvrirRapport} data-testid="ouvrir-rapport"
                    className="w-full" style={{ minHeight: 48 }}>
              <FileText className="w-5 h-5 mr-2" /> Rapport de visite
            </Button>
            {(resultat.devis?.id || resultat.devis?.devis_id) && (
              <Button variant="outline" className="w-full" style={{ minHeight: 48 }}
                      data-testid="ouvrir-devis"
                      onClick={() => openAuthedPdf({
                        url: `${API}/api/metier/devis/${resultat.devis.id || resultat.devis.devis_id}/pdf`,
                        headers: entetes(),
                        onError: (m) => toast.error(m),
                      })}>
                <Receipt className="w-5 h-5 mr-2" /> Devis
              </Button>
            )}
          </div>
        )}
      </main>

      {/* ── Barre d'avance : ⛔ AUCUNE étape n'est bloquée ── */}
      {etape < 9 && (
        <footer className="fixed bottom-0 left-0 right-0 px-4 py-3 flex gap-2"
                style={{ background: "var(--oh-bg-elevated)",
                         borderTop: "0.5px solid var(--oh-border)" }}>
          {etape > 1 && (
            <Button variant="outline" onClick={() => setEtape((e) => (e === 8 ? 5 : e - 1))}
                    style={{ minHeight: 48 }} data-testid="pied-retour">
              <ChevronLeft className="w-5 h-5" /> Retour
            </Button>
          )}
          {etape === 5 ? (
            <Button className="flex-1" style={{ minHeight: 48 }} data-testid="poste-suivant"
                    onClick={() => {
                      // 🔴 Étape 7 : poste suivant, jusqu'à la fin.
                      if (idxPoste + 1 < postesRetenus.length) setIdxPoste((i) => i + 1);
                      else setEtape(8);
                    }}>
              {idxPoste + 1 < postesRetenus.length ? "Poste suivant" : "Terminer les postes"}
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : etape === 8 ? (
            <Button className="flex-1" style={{ minHeight: 48 }} disabled={envoi}
                    data-testid="valider-visite" onClick={valider}>
              {envoi ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5 mr-1" />}
              Valider
            </Button>
          ) : (
            <Button className="flex-1" style={{ minHeight: 48 }} data-testid="etape-suivante"
                    onClick={() => setEtape((e) => e + 1)}>
              Continuer <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </footer>
      )}
    </div>
  );
}
